import { useNotify, useRecordContext, useRefresh, useUpdate } from "ra-core";
import { Building, FileText } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

import { ContactStatusSelector } from "./ContactInputs";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { TagsListEdit } from "./TagsListEdit";
import { acquisitionLabel } from "./acquisition";
import { EditableField } from "../shared/EditableField";
import type { Contact, ContactSource } from "../types";

// Sprint 7 T7.4.a — Refonte DetailsTab avec EditableField inline.
// Cf design/atomic-crm/details.jsx — chaque ligne devient cliquable et
// passe en mode édition avec input/select + boutons annuler/save.
//
// Champs éditables : title (poste), background (textarea), source
// (select). Le reste reste readonly avec badge ou redirection vers le
// header de la fiche (population/température/prochaine action sont
// éditables là).

const SOURCE_OPTIONS = [
  { value: "linkedin", label: "LinkedIn" },
  { value: "instantly", label: "Instantly" },
  { value: "facebook", label: "Facebook / Bouche-à-oreille" },
  { value: "plateforme", label: "Plateforme fabrik.so" },
  { value: "telephone", label: "Téléphone" },
  { value: "autre", label: "Autre" },
];

const SOURCE_LABEL: Record<string, string> = Object.fromEntries(
  SOURCE_OPTIONS.map((o) => [o.value, o.label]),
);

const TEMPERATURE_LABEL: Record<string, string> = {
  chaud: "Chaud",
  a_chauffer: "À chauffer",
  froid: "Froid",
  perdu: "Perdu",
};

const POPULATION_LABEL: Record<string, string> = {
  plateforme: "Plateforme",
  prospection: "Prospection",
};

const NEXT_ACTION_LABEL: Record<string, string> = {
  a_relancer: "À relancer",
  a_appeler: "À appeler",
  a_mailer: "Envoyer démo",
  a_engager: "À engager",
  aucune: "Aucune action",
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="text-[10.5px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
        {title}
      </h3>
      <Separator className="mb-3" />
      {children}
    </section>
  );
}

export function ContactDetailsTab() {
  const record = useRecordContext<Contact>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate();
  if (!record) return null;

  const saveField = async (key: keyof Contact, next: string | null) => {
    try {
      await update(
        "contacts",
        {
          id: record.id,
          data: { [key]: next },
          previousData: record,
        },
        { returnPromise: true },
      );
      notify("Modifié", { type: "success" });
      refresh();
    } catch (err) {
      notify(`Échec de la mise à jour : ${(err as Error).message}`, {
        type: "error",
      });
    }
  };

  const nextActionLabel = record.next_action_type
    ? (NEXT_ACTION_LABEL[record.next_action_type] ?? record.next_action_type)
    : null;

  return (
    <Card>
      <CardContent className="py-5 space-y-6">
        <Section title="Status commercial">
          <ContactStatusSelector />
        </Section>

        <Section title="Informations personnelles">
          <ContactPersonalInfo />
        </Section>

        <Section title="Rôle">
          <EditableField
            label="Poste"
            value={record.title}
            onSave={(next) => saveField("title", next)}
          />
          <EditableField
            label="Background"
            value={record.background}
            displayValue={
              record.background ? (
                <span className="whitespace-pre-line">{record.background}</span>
              ) : undefined
            }
            type="textarea"
            icon={<FileText />}
            onSave={(next) => saveField("background", next)}
          />
        </Section>

        <Section title="Classification">
          <EditableField
            label="Population"
            value={record.population}
            displayValue={
              POPULATION_LABEL[record.population] ?? record.population
            }
            readonly
          />
          <EditableField
            label="Température"
            value={record.temperature ?? ""}
            displayValue={
              record.temperature
                ? TEMPERATURE_LABEL[record.temperature]
                : "Non définie"
            }
            readonly
          />
          <EditableField
            label="Source"
            value={record.source}
            displayValue={SOURCE_LABEL[record.source] ?? record.source}
            type="select"
            options={SOURCE_OPTIONS}
            onSave={(next) => saveField("source", next as ContactSource)}
          />
          <EditableField
            label="Acquisition"
            value={record.acquisition_source ?? ""}
            displayValue={acquisitionLabel(record.acquisition_source)}
            readonly
          />
          <EditableField
            label="Lead source d'origine"
            value={record.lead_source}
            readonly
          />
          <EditableField
            label="Prochaine action"
            value={nextActionLabel ?? ""}
            displayValue={
              nextActionLabel && record.next_action_type !== "aucune" ? (
                <>
                  {nextActionLabel}
                  {record.next_action_date && (
                    <span className="text-muted-foreground ml-2">
                      le {formatDate(record.next_action_date)}
                    </span>
                  )}
                </>
              ) : (
                <em className="text-muted-foreground">Aucune action prévue</em>
              )
            }
            readonly
          />
          <p className="text-[11.5px] text-muted-foreground mt-2 ml-[180px]">
            Population, Température et Prochaine action s'éditent via les badges
            du header.
          </p>
        </Section>

        <Section title="Tags">
          <TagsListEdit />
        </Section>

        <Section title="Lien et entreprise">
          <EditableField
            label="LinkedIn"
            value={record.linkedin_url}
            type="url"
            icon={<Building />}
            suffix={
              record.linkedin_url ? (
                <a
                  href={record.linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11.5px] text-muted-foreground hover:text-foreground underline ml-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  ouvrir
                </a>
              ) : null
            }
            onSave={(next) => saveField("linkedin_url", next)}
          />
        </Section>

        <Section title="Historique">
          <EditableField
            label="Première vue"
            value={record.first_seen}
            displayValue={formatDate(record.first_seen)}
            readonly
          />
          <EditableField
            label="Dernière vue"
            value={record.last_seen}
            displayValue={formatDate(record.last_seen)}
            readonly
          />
        </Section>
      </CardContent>
    </Card>
  );
}
